import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import EpsfPageBreadcrumb from "../epsf/EpsfPageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ConfirmModal from "../../common/ConfirmModal";
import { openAuthModal } from "../../common/authmodal/authmodalservice";
import { fetchRoleLookup, isRoleGuid } from "../../../services/lookupService";
import { useUserData } from "../../../hooks/useUserData";
import {
  canAddMilestone,
  canDeleteMilestone,
} from "./isfPermissionUtils";
import {
  deleteMilestone,
  extractMilestoneApiMessage,
  resolveMilestoneDuplicateCodeError,
  DUPLICATE_MILESTONE_CODE_ERROR,
  fetchMilestoneList,
  fetchMilestoneLookup,
  formatMilestoneListAnchorLabel,
  fetchMilestoneUsagePlans,
  fetchMilestoneUsageTemplates,
  fetchMilestoneUsageStudies,
  isMilestoneInUse,
  isMilestoneListResponseBroken,
  normalizeMilestoneList,
  normalizeMilestoneLookup,
  normalizeMilestoneUsagePlans,
  normalizeMilestoneUsageTemplates,
  normalizeMilestoneUsageStudies,
  parseTags,
  saveMilestone,
  statusBadgeClass,
  buildMilestoneSavePayload,
  resolveDefaultNotificationRecipientIds,
  resolveDefaultPiRoleId,
  validateMilestoneNotifications,
  validateMilestoneDefaultFolder,
  validateMilestoneAnchor,
  validateMilestoneUploaderRoles,
  validateMilestoneRoleSelection,
  NOTIFICATION_ROLE_FALLBACK,
} from "./isfMilestoneService";
import {
  FOLDER_LIBRARY_CATEGORIES,
  fetchFolderLibrary,
  folderLibraryToOptions,
  isFolderLibrarySuccess,
  normalizeFolderCategory,
  normalizeFolderLibraryList,
} from "./isfFolderLibraryService";
import {
  emptyMilestoneForm,
  resolveMilestoneFolderCategory,
} from "./milestoneLibraryShared";
import MilestoneLibraryForm from "./MilestoneLibraryForm";
import MilestoneEditorModal from "./milestoneLibraryDetail";
import MilestoneUsageModal from "./MilestoneUsageModal";
import StudyGateBadge from "../../common/StudyGateBadge/StudyGateBadge";
import {
  ISF_REPORT_ACTIONS_COLUMN,
  ISF_REPORT_PINNED_COLUMNS,
  matchTextColumnSearch,
} from "./isfReportTable";
import "./isfReportTable.scss";
import "./milestoneLibrary.scss";

const MILESTONE_LIBRARY_MILESTONE_COLUMN = "Milestone";

const MILESTONE_LIBRARY_FIELD_OPTIONS = [
  { key: MILESTONE_LIBRARY_MILESTONE_COLUMN, label: "Milestone" },
  { key: "Anchor", label: "Anchor" },
  { key: "GateCode", label: "Gate" },
  { key: "DefaultFolderCategory", label: "Binder" },
  { key: "Status", label: "Status" },
  { key: "Category", label: "Category" },
  { key: "UsedInPlans", label: "Used In Plans" },
  { key: "UsedOnTemplates", label: "Templates" },
  { key: "UsedInStudies", label: "Studies" },
  { key: ISF_REPORT_ACTIONS_COLUMN, label: "Actions" },
];

const DEFAULT_MILESTONE_LIBRARY_COLUMNS = [
  MILESTONE_LIBRARY_MILESTONE_COLUMN,
  "Anchor",
  "GateCode",
  "DefaultFolderCategory",
  "Status",
  "Category",
  "UsedInPlans",
  "UsedOnTemplates",
  "UsedInStudies",
  ISF_REPORT_ACTIONS_COLUMN,
];

const milestoneBinderBadgeClass = (category) => {
  const normalized = normalizeFolderCategory(category);
  const slug = normalized === "ePSF" ? "epsf" : "eisf";
  return `milestone-binder-badge milestone-binder-badge--${slug}`;
};

const renderMilestoneLibraryMilestoneCell = (row, onOpen) => {
  const code = String(row.Code ?? row.MilestoneCode ?? "").trim();
  const name = String(row.Name ?? row.MilestoneName ?? "").trim() || "—";
  const fullTitle = code ? `${code} — ${name}` : name;

  return (
    <button
      type="button"
      className="report-table__cell-link milestone-library-report__milestone-cell"
      title={fullTitle}
      onClick={() => onOpen?.(row)}
    >
      {code ? (
        <span className="milestone-library-report__code-badge">{code}</span>
      ) : null}
      <span className="milestone-library-report__milestone-name">{name}</span>
    </button>
  );
};

const MilestoneLibrary = ({ lockedCategory = null } = {}) => {
  const navigate = useNavigate();
  const { milestoneId } = useParams();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const lock = lockedCategory
    ? normalizeFolderCategory(lockedCategory)
    : null;
  const libraryBasePath = lock === "ePSF" ? "/epsf/milestone-library" : "/milestone-library";

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [binderFilters, setBinderFilters] = useState(lock ? [lock] : []);
  const [milestoneLookup, setMilestoneLookup] = useState([]);
  const [notificationRoles, setNotificationRoles] = useState(NOTIFICATION_ROLE_FALLBACK);
  const [folderOptions, setFolderOptions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyMilestoneForm());
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({ open: false, row: null });
  const [usageModal, setUsageModal] = useState({
    open: false,
    milestone: null,
    plans: [],
    templates: [],
    studies: [],
  });
  const [tagInput, setTagInput] = useState("");
  const createModalBodyRef = useRef(null);

  const canAdd = userData && canAddMilestone(userData);
  const canDelete = userData && canDeleteMilestone(userData);

  const loadList = async () => {
    if (!userData?.OrganizationId) return;
    try {
      setListLoading(true);
      const response = await fetchMilestoneList({ userData });
      if (response?.success !== 1) {
        showAlert({
          title: "Error",
          message:
            extractMilestoneApiMessage(
              response,
              "Failed to load milestones. The milestone database script may need to be applied.",
            ),
          type: "danger",
        });
        setRows([]);
        return;
      }

      if (isMilestoneListResponseBroken(response)) {
        showAlert({
          title: "Error",
          message:
            "Failed to load milestones. The milestone database script may need to be applied (GateCode / usp_EISF_Milestone_NEWUI).",
          type: "danger",
        });
        setRows([]);
        return;
      }

      setRows(normalizeMilestoneList(response));
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load milestones.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  const loadLookup = async () => {
    if (!userData?.OrganizationId) return;
    try {
      const response = await fetchMilestoneLookup({ userData });
      if (response?.success === 1) {
        setMilestoneLookup(normalizeMilestoneLookup(response));
      }
    } catch {
      setMilestoneLookup([]);
    }
  };

  const loadNotificationRoles = async () => {
    if (!userData?.OrganizationId) return;
    try {
      const roles = await fetchRoleLookup(userData);
      if (roles.length) setNotificationRoles(roles);
    } catch {
      setNotificationRoles(NOTIFICATION_ROLE_FALLBACK);
    }
  };

  const loadFolderOptions = async () => {
    if (!userData?.OrganizationId) return;
    try {
      const response = await fetchFolderLibrary({ userData });
      if (isFolderLibrarySuccess(response)) {
        setFolderOptions(folderLibraryToOptions(normalizeFolderLibraryList(response)));
      }
    } catch {
      setFolderOptions([]);
    }
  };

  useEffect(() => {
    if (userData?.OrganizationId) {
      loadList();
      loadLookup();
      loadNotificationRoles();
      loadFolderOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId]);

  useEffect(() => {
    if (!showCreateModal || !notificationRoles.length) return;

    const defaultIds = resolveDefaultNotificationRecipientIds(notificationRoles);
    const piRoleId = resolveDefaultPiRoleId(notificationRoles);

    setForm((prev) => {
      const notifications = prev.notifications || [];
      const needsNotificationDefaults = notifications.every(
        (rule) => !String(rule.DefaultRecipients || "").trim(),
      );
      const needsUploaderDefault = !String(prev.DefaultUploaderRoles || "").trim();
      const needsSignerDefault = !String(prev.DefaultSignerRoles || "").trim();

      if (
        !needsNotificationDefaults &&
        !(needsUploaderDefault && piRoleId) &&
        !(needsSignerDefault && piRoleId)
      ) {
        return prev;
      }

      const next = { ...prev };

      if (needsNotificationDefaults && defaultIds.length) {
        const recipients = defaultIds.join(",");
        next.notifications = notifications.map((rule) => ({
          ...rule,
          DefaultRecipients: recipients,
        }));
      }

      if (needsUploaderDefault && piRoleId) {
        next.DefaultUploaderRoles = piRoleId;
      }

      if (needsSignerDefault && piRoleId) {
        next.DefaultSignerRoles = piRoleId;
      }

      return next;
    });
  }, [showCreateModal, notificationRoles]);

  const resetCreateForm = () => {
    const next = emptyMilestoneForm(notificationRoles);
    if (lock) next.DefaultFolderCategory = lock;
    setForm(next);
    setErrors({});
    setTagInput("");
  };

  const openCreate = () => {
    resetCreateForm();
    setShowCreateModal(true);
  };

  const scrollCreateModalToTop = () => {
    requestAnimationFrame(() => {
      createModalBodyRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const getFirstMilestoneFormError = (nextErrors) =>
    nextErrors.MilestoneCode ||
    nextErrors.MilestoneName ||
    nextErrors.GateCode ||
    nextErrors.DefaultFolderName ||
    nextErrors.AnchorMilestoneCode ||
    nextErrors.notifications ||
    nextErrors.DefaultUploaderRoles ||
    nextErrors.DefaultSignerRoles ||
    null;

  const validate = () => {
    const next = {};
    const code = form.MilestoneCode.trim().toUpperCase();
    if (!code) next.MilestoneCode = "Code is required.";
    else if (
      rows.some(
        (row) => String(row.Code ?? row.MilestoneCode ?? "").trim().toUpperCase() === code,
      )
    ) {
      next.MilestoneCode = DUPLICATE_MILESTONE_CODE_ERROR;
    }
    if (!form.MilestoneName.trim()) next.MilestoneName = "Name is required.";
    if (!String(form.GateCode || "").trim()) {
      next.GateCode = "Study gate is required.";
    }

    const folderError = validateMilestoneDefaultFolder(form.DefaultFolderName);
    if (folderError) next.DefaultFolderName = folderError;

    const anchorError = validateMilestoneAnchor(
      form.AnchorType,
      form.AnchorMilestoneCode,
    );
    if (anchorError) next.AnchorMilestoneCode = anchorError;

    const notificationErrors = validateMilestoneNotifications(
      form.notifications,
      notificationRoles,
    );
    if (notificationErrors.length) {
      next.notifications = notificationErrors[0];
    }

    const uploaderError = validateMilestoneUploaderRoles(
      form.DefaultUploaderRoles,
      notificationRoles,
    );
    if (uploaderError) next.DefaultUploaderRoles = uploaderError;

    if (form.RequiresSignature) {
      const signerError = validateMilestoneRoleSelection(
        form.DefaultSignerRoles,
        notificationRoles,
        "signer",
        true,
      );
      if (signerError) next.DefaultSignerRoles = signerError;
    }

    setErrors(next);
    const firstError = getFirstMilestoneFormError(next);
    return { ok: Object.keys(next).length === 0, firstError };
  };

  const handleCreate = async () => {
    if (!canAdd) return;

    const { ok, firstError } = validate();
    if (!ok) {
      scrollCreateModalToTop();
      showAlert({
        title: "Cannot save",
        message: firstError || "Please fix the highlighted fields.",
        type: "warning",
      });
      return;
    }

    const payload = buildMilestoneSavePayload(form);

    try {
      showLoader("Saving...");
      const response = await saveMilestone({ userData, payload });
      if (response?.success === 1) {
        showAlert({
          title: "Success",
          message: "Milestone saved.",
          type: "success",
        });
        setShowCreateModal(false);
        resetCreateForm();
        await loadList();
        await loadLookup();
      } else {
        const duplicateError = resolveMilestoneDuplicateCodeError(response);
        if (duplicateError) {
          setErrors((prev) => ({ ...prev, MilestoneCode: duplicateError }));
          scrollCreateModalToTop();
          showAlert({
            title: "Error",
            message: duplicateError,
            type: "danger",
          });
        } else {
          const message = extractMilestoneApiMessage(response, "Save failed.");
          showAlert({
            title: "Error",
            message,
            type: "danger",
          });
        }
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Save failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteConfirm = async () => {
    const row = confirmModal.row;
    if (!row?.MilestoneId) return;
    setConfirmModal({ open: false, row: null });

    const authRes = await openAuthModal({
      showDescription: true,
      requireDescription: true,
    });
    if (authRes?.Success !== 1) return;

    const reason = String(authRes?.Description || "").trim();
    if (!reason) return;

    try {
      showLoader("Deleting...");
      const response = await deleteMilestone({
        userData,
        milestoneId: row.MilestoneId,
        reason,
      });
      if (response?.success === 1) {
        showAlert({ title: "Success", message: "Milestone deleted.", type: "success" });
        await loadList();
      } else {
        showAlert({
          title: "Error",
          message: response?.message || "Delete failed.",
          type: "danger",
        });
      }
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Delete failed.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const updateNotification = (index, field, value) => {
    setForm((prev) => {
      const list = [...(prev.notifications || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, notifications: list };
    });
  };

  const setRecipients = (index, roleIds) => {
    setForm((prev) => {
      const list = [...(prev.notifications || [])];
      list[index] = {
        ...list[index],
        DefaultRecipients: [...new Set((roleIds || []).filter(isRoleGuid))].join(","),
      };
      return { ...prev, notifications: list };
    });
  };

  const setUploaderRoles = (roleIds) => {
    setForm((prev) => ({
      ...prev,
      DefaultUploaderRoles: [...new Set((roleIds || []).filter(isRoleGuid))].join(","),
    }));
  };

  const setSignerRoles = (roleIds) => {
    setForm((prev) => ({
      ...prev,
      DefaultSignerRoles: [...new Set((roleIds || []).filter(isRoleGuid))].join(","),
    }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    const existing = parseTags(form.Tags);
    if (existing.includes(tag)) {
      setTagInput("");
      return;
    }
    setForm((p) => ({ ...p, Tags: [...existing, tag].join(",") }));
    setTagInput("");
  };

  const removeTag = (tag) => {
    setForm((p) => ({
      ...p,
      Tags: parseTags(p.Tags)
        .filter((t) => t !== tag)
        .join(","),
    }));
  };

  const closeUsageModal = () =>
    setUsageModal({ open: false, milestone: null, plans: [], templates: [], studies: [] });

  const openUsageInfo = async (row) => {
    const planCount = Number(row?.UsedInPlans) || 0;
    const templateCount = Number(row?.UsedOnTemplates) || 0;
    const studyCount = Number(row?.UsedInStudies) || 0;
    if (!row?.MilestoneId || (planCount <= 0 && templateCount <= 0 && studyCount <= 0)) return;

    try {
      showLoader("Loading usage...");
      const [plansResponse, templatesResponse, studiesResponse] = await Promise.all([
        planCount > 0
          ? fetchMilestoneUsagePlans({ userData, milestoneId: row.MilestoneId })
          : Promise.resolve({ success: 1 }),
        templateCount > 0
          ? fetchMilestoneUsageTemplates({ userData, milestoneId: row.MilestoneId })
          : Promise.resolve({ success: 1 }),
        studyCount > 0
          ? fetchMilestoneUsageStudies({ userData, milestoneId: row.MilestoneId })
          : Promise.resolve({ success: 1 }),
      ]);

      if (
        plansResponse?.success !== 1 ||
        templatesResponse?.success !== 1 ||
        studiesResponse?.success !== 1
      ) {
        showAlert({
          title: "Error",
          message:
            plansResponse?.message ||
            templatesResponse?.message ||
            studiesResponse?.message ||
            "Failed to load milestone usage.",
          type: "danger",
        });
        return;
      }

      setUsageModal({
        open: true,
        milestone: row,
        plans: planCount > 0 ? normalizeMilestoneUsagePlans(plansResponse) : [],
        templates:
          templateCount > 0 ? normalizeMilestoneUsageTemplates(templatesResponse) : [],
        studies: studyCount > 0 ? normalizeMilestoneUsageStudies(studiesResponse) : [],
      });
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Failed to load milestone usage.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const renderUsageBadge = (row, count, label, title) => {
    const clickable = count > 0;
    return (
      <button
        type="button"
        className={`milestone-used-plans-badge${
          clickable
            ? " milestone-used-plans-badge--clickable"
            : " milestone-used-plans-badge--zero"
        }`}
        onClick={() => openUsageInfo(row)}
        disabled={!clickable}
        title={clickable ? title : undefined}
        aria-label={clickable ? `${count} ${label} — click to view` : `Not used in any ${label}`}
      >
        {count}
      </button>
    );
  };

  const openMilestone = useCallback(
    (row) => {
      if (!row?.MilestoneId) return;
      navigate(`/milestone-library/${row.MilestoneId}`);
    },
    [navigate],
  );

  const closeEditor = useCallback(() => {
    navigate("/milestone-library");
  }, [navigate]);

  const navigateEditorMilestone = useCallback(
    (id) => {
      if (!id) return;
      navigate(`${libraryBasePath}/${id}`);
    },
    [navigate],
  );

  const rowsWithBinder = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        DefaultFolderCategory: resolveMilestoneFolderCategory(row, folderOptions),
      })),
    [rows, folderOptions],
  );

  const filteredRows = useMemo(() => {
    const byBinder = !binderFilters.length
      ? rowsWithBinder
      : rowsWithBinder.filter((row) =>
          binderFilters.includes(row.DefaultFolderCategory),
        );
    if (!lock) return byBinder;
    return byBinder.filter((row) => row.DefaultFolderCategory === lock);
  }, [rowsWithBinder, binderFilters, lock]);

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === ISF_REPORT_ACTIONS_COLUMN) {
        if (!canDelete || isMilestoneInUse(row)) return null;
        return (
          <button
            type="button"
            className="isf-template-delete-icon"
            title="Delete milestone"
            aria-label={`Delete ${row.Code || row.Name || "milestone"}`}
            onClick={(event) => {
              event.stopPropagation();
              setConfirmModal({ open: true, row });
            }}
          >
            <i className="fas fa-trash-alt" aria-hidden="true" />
          </button>
        );
      }
      if (field === MILESTONE_LIBRARY_MILESTONE_COLUMN) {
        return renderMilestoneLibraryMilestoneCell(row, openMilestone);
      }
      if (field === "GateCode") {
        return <StudyGateBadge code={row.GateCode ?? row.gateCode} compact />;
      }
      if (field === "DefaultFolderCategory") {
        const binder = resolveMilestoneFolderCategory(row, folderOptions);
        return (
          <span className={milestoneBinderBadgeClass(binder)} title={`Binder: ${binder}`}>
            {binder}
          </span>
        );
      }
      if (field === "Anchor") {
        return formatMilestoneListAnchorLabel(row);
      }
      if (field === "UsedInPlans") {
        return renderUsageBadge(
          row,
          Number(row.UsedInPlans) || 0,
          "plan(s)",
          "View plans using this milestone",
        );
      }
      if (field === "UsedOnTemplates") {
        return renderUsageBadge(
          row,
          Number(row.UsedOnTemplates) || 0,
          "template(s)",
          "View templates using this milestone",
        );
      }
      if (field === "UsedInStudies") {
        return renderUsageBadge(
          row,
          Number(row.UsedInStudies) || 0,
          "study eISF(s)",
          "View studies using this milestone in eISF folders",
        );
      }
      if (field === "Status") {
        const status = row.Status || "—";
        const version = row.Version ?? row.version;
        return (
          <span className={statusBadgeClass(row.Status)}>
            {status}
            {version != null && String(version).trim() !== "" ? (
              <span className="milestone-status-badge__version"> v{version}</span>
            ) : null}
          </span>
        );
      }
      return String(row[field] ?? "—");
    },
    [folderOptions, canDelete, openMilestone],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;

    if (field === MILESTONE_LIBRARY_MILESTONE_COLUMN) {
      const code = String(row.Code ?? row.MilestoneCode ?? "")
        .trim()
        .toLowerCase();
      const name = String(row.Name ?? row.MilestoneName ?? "")
        .trim()
        .toLowerCase();
      return code.includes(term) || name.includes(term);
    }

    if (field === "Status") {
      const status = String(row.Status ?? "").trim().toLowerCase();
      const version = String(row.Version ?? row.version ?? "").trim().toLowerCase();
      return status.includes(term) || version.includes(term);
    }

    return matchTextColumnSearch(row, field, searchValue, [
      ISF_REPORT_ACTIONS_COLUMN,
      "DefaultFolderCategory",
      "GateCode",
      "Anchor",
      "UsedInPlans",
      "UsedOnTemplates",
      "UsedInStudies",
    ]);
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DefaultFolderCategory") {
        if (options?.checkOnly) return true;
        if (lock) {
          return (
            <span className="text-muted small">{lock}</span>
          );
        }
        return (
          <BadgeMultiSelect
            options={FOLDER_LIBRARY_CATEGORIES}
            value={binderFilters}
            onChange={setBinderFilters}
            emptyLabel="All binders"
            addLabel="+"
            popoverTitle="Filter by binder"
            popoverSubtitle="Select eISF and/or ePSF"
            compactSummary
            disabled={options?.disabled}
          />
        );
      }
      return null;
    },
    [binderFilters, lock],
  );

  const getFieldLabel = useCallback(
    (field) =>
      MILESTONE_LIBRARY_FIELD_OPTIONS.find((option) => option.key === field)
        ?.label || field,
    [],
  );

  const layoutStorageKey = userData?.OrganizationId
    ? `isf-milestone-library-report-v4-${userData.OrganizationId}`
    : "";
  const CrumbBar = lock === "ePSF" ? EpsfPageBreadcrumb : PageBreadcrumb;

  return (
    <div className="milestone-page">
      <CrumbBar
        items={
          lock
            ? [
                { label: "ePSF", path: "/epsf/imp/inventory" },
                { label: "Milestone library" },
              ]
            : [
                { label: "Home", path: "/notification-center" },
                { label: "Milestone Library" },
              ]
        }
        actions={[
          { icon: "fas fa-refresh", label: "Refresh", onClick: loadList },
          ...(canAdd
            ? [{ icon: "fas fa-plus", label: "Add Milestone", onClick: openCreate }]
            : []),
        ]}
      />

      <div className="isf-report-table-wrap">
        <ReportTable
          rows={filteredRows}
          fieldOptions={MILESTONE_LIBRARY_FIELD_OPTIONS}
          defaultColumns={DEFAULT_MILESTONE_LIBRARY_COLUMNS}
          getFieldLabel={getFieldLabel}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          frozenColumnWidthRem={14}
          pinnedRightColumns={ISF_REPORT_PINNED_COLUMNS}
          pinnedRightColumnWidthRem={4.5}
          hideAddColumnFields={[ISF_REPORT_ACTIONS_COLUMN]}
          hideColumnHeaderActionsFields={[ISF_REPORT_ACTIONS_COLUMN]}
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      {milestoneId ? (
        <MilestoneEditorModal
          milestoneId={milestoneId}
          lockedCategory={lock}
          onClose={closeEditor}
          onChanged={loadList}
          onNavigateMilestone={navigateEditorMilestone}
        />
      ) : null}

      {showCreateModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog milestone-modal-dialog milestone-modal-dialog--full">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {lock ? `Add ${lock} Milestone` : "Add Milestone"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                />
              </div>
              <div className="modal-body milestone-modal-body" ref={createModalBodyRef}>
                <MilestoneLibraryForm
                  form={form}
                  setForm={setForm}
                  errors={errors}
                  readOnly={false}
                  lockedCategory={lock}
                  notificationRoles={notificationRoles}
                  folderOptions={folderOptions}
                  milestoneLookup={milestoneLookup}
                  tagInput={tagInput}
                  setTagInput={setTagInput}
                  onAddTag={addTag}
                  onRemoveTag={removeTag}
                  onUpdateNotification={updateNotification}
                  onSetRecipients={setRecipients}
                  onSetUploaderRoles={setUploaderRoles}
                  onSetSignerRoles={setSignerRoles}
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetCreateForm();
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleCreate}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.open}
        title="Delete milestone"
        message={`Are you sure you want to delete milestone "${confirmModal.row?.Code || ""}"?`}
        confirmText="Delete"
        confirmButtonClass="btn-danger"
        onConfirm={handleDeleteConfirm}
        onClose={() => setConfirmModal({ open: false, row: null })}
      />

      <MilestoneUsageModal
        open={usageModal.open}
        milestone={usageModal.milestone}
        plans={usageModal.plans}
        templates={usageModal.templates}
        studies={usageModal.studies}
        onClose={closeUsageModal}
      />

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default MilestoneLibrary;
